import { useState, useMemo } from 'react';
import {
  Drawer,
  Descriptions,
  Spin,
  Empty,
  Table,
  Tag,
  Button,
  Tooltip,
  Tabs,
  message,
  Grid,
  Typography,
  Modal,
  Form,
  Input,
  Select,
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
  useFetchChargerHistory,
} from '../Pages/charging-history/Hooks';
import {
  useFetchCustomerDetails,
  useGetCustomerPin,
  useResetCustomerPin,
} from '../Pages/customers/Hooks';
import {
  useCreateCustomerVehicle,
  useFetchCustomerVehicles,
  useFetchEvVehicleBrands,
  useFetchEvVehicleModels,
} from '../Pages/ev-vehicle-models/Hooks';
import { unixToLocalDate } from '../utils/CommonFunctions';
import { formatCurrency } from '../utils/utils';
import TransactionDetailsDrawer from './Modals/TransactionDetailsModal';
import ViewRFIDModal from '../components/Modals/ViewRFIDModal';
import AddRFIDModalDirectlyUser from './Modals/AddRFIDModalDirectlyUser';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { BarChartOutlined, SyncOutlined, EyeInvisibleOutlined, EyeOutlined, CopyOutlined, PlusOutlined } from '@ant-design/icons';
import SingleChargerDrawer from '../Pages/locations/[id]/SingleChargerDrawer';
import { useOperatorStore } from '../store/useOperatorStore';
import { useAuthStore } from '../store/useAuthStore';
const { Title } = Typography;
const { useBreakpoint } = Grid;

const CustomerDrawerView = ({ open, onClose, id, operatorId: operatorIdFromParent }) => {
  const { t } = useTranslation('CustomerView');
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [transactionId, setTransactionId] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [rfidToView, setRfidToView] = useState(null);
  const [chargerDrawerOpen, setChargerDrawerOpen] = useState(false);
  const [chargerDetails, setChargerDetails] = useState(null);
  const [transactionModalOpen, setTransactionModalOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { operatorId: operatorIdFromStore } = useOperatorStore();
  const operatorId = operatorIdFromParent ?? operatorIdFromStore;
  const { user } = useAuthStore();
  const isReadOnlyUser = user?.role === 'READ_ONLY_ADMIN';
  const [pinValue, setPinValue] = useState(null);
  const [isPinVisible, setIsPinVisible] = useState(false);
  const [resetPinModalOpen, setResetPinModalOpen] = useState(false);
  const [addVehicleOpen, setAddVehicleOpen] = useState(false);
  const [vehicleBrand, setVehicleBrand] = useState(null);
  const [vehicleForm] = Form.useForm();

  const { data: customer, isLoading: isCustomerDetailsLoading } =
    useFetchCustomerDetails(id, operatorId);
  /* eslint-enable no-console */
  const { mutate: getPin, isPending: isGettingPin } = useGetCustomerPin({
    onSuccess: (data) => {
      setPinValue(data?.pin);
      setIsPinVisible(true);
    },
    onError: (error) => {
      message.error(error?.response?.data?.message || t('FailedToGetPin'));
    }
  });

  const { mutate: resetPin, isPending: isResettingPin } = useResetCustomerPin({
    onSuccess: () => {
      message.success(t('PinResetSuccess'));
      setResetPinModalOpen(false);
      if (customer?._id) {
        getPin({ id: customer._id });
      }
    },
    onError: (error) => {
      message.error(error?.response?.data?.message || t('PinResetFailed'));
    }
  });


  const customerIdForHistory = customer?._id ?? customer?.id;

  const pagination = useMemo(
    () => ({
      limit: 5,
      offset: page,
      searchType: 'CUSTOMER',
      searchValue: customerIdForHistory,
    }),
    [customerIdForHistory, page]
  );

  const {
    data: transactions = { paginatedData: [], totalCount: 0 },
    isLoading,
    isFetching,
  } = useFetchChargerHistory(pagination, operatorId);

  const {
    data: vehiclesData,
    isLoading: vehiclesLoading,
    refetch: refetchVehicles,
  } = useFetchCustomerVehicles(customerIdForHistory, {
    enabled: open && !!customerIdForHistory,
  });
  const { data: brands = [] } = useFetchEvVehicleBrands({
    enabled: addVehicleOpen,
  });
  const { data: modelsData } = useFetchEvVehicleModels(
    { brand: vehicleBrand || '', page: 1, limit: 100 },
    { enabled: addVehicleOpen && !!vehicleBrand }
  );
  const { mutate: createVehicle, isPending: creatingVehicle } =
    useCreateCustomerVehicle({
      onSuccess: () => {
        message.success(t('VehicleAdded'));
        setAddVehicleOpen(false);
        vehicleForm.resetFields();
        setVehicleBrand(null);
        refetchVehicles();
      },
      onError: (error) => {
        message.error(error?.response?.data?.message || t('VehicleAddFailed'));
      },
    });

  const vehicleColumns = [
    {
      title: t('VehicleName'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('VehicleNumber'),
      dataIndex: 'vehicleNumber',
      key: 'vehicleNumber',
    },
    {
      title: t('VehicleModel'),
      key: 'model',
      render: (_, row) => {
        const model = row.evVehicleModelId;
        if (!model || typeof model !== 'object') return '—';
        return [model.brand, model.model || model.name].filter(Boolean).join(' ');
      },
    },
  ];

  const renderVehiclesContent = () => (
    <div>
      {!isReadOnlyUser && (
        <div className="mb-3 flex justify-end">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setAddVehicleOpen(true)}
          >
            {t('AddVehicle')}
          </Button>
        </div>
      )}
      <Table
        rowKey="_id"
        loading={vehiclesLoading}
        columns={vehicleColumns}
        dataSource={vehiclesData?.paginatedData || []}
        locale={{ emptyText: <Empty description={t('NoVehicles')} /> }}
        pagination={false}
        size="small"
      />
      <Modal
        title={<Title level={4} className="mb-0!">{t('AddVehicle')}</Title>}
        open={addVehicleOpen}
        onCancel={() => {
          setAddVehicleOpen(false);
          vehicleForm.resetFields();
          setVehicleBrand(null);
        }}
        onOk={async () => {
          const values = await vehicleForm.validateFields();
          createVehicle({
            customerId: customerIdForHistory,
            name: values.name,
            vehicleNumber: values.vehicleNumber,
            ...(values.evVehicleModelId
              ? { evVehicleModelId: values.evVehicleModelId }
              : {}),
          });
        }}
        confirmLoading={creatingVehicle}
        destroyOnClose
      >
        <Form form={vehicleForm} layout="vertical">
          <Form.Item
            name="name"
            label={t('VehicleName')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="vehicleNumber"
            label={t('VehicleNumber')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label={t('VehicleBrand')}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t('OptionalBrand')}
              value={vehicleBrand}
              onChange={(value) => {
                setVehicleBrand(value || null);
                vehicleForm.setFieldValue('evVehicleModelId', undefined);
              }}
              options={brands.map((item) => ({
                label: item.brand,
                value: item.brand,
              }))}
            />
          </Form.Item>
          <Form.Item name="evVehicleModelId" label={t('VehicleModel')}>
            <Select
              allowClear
              disabled={!vehicleBrand}
              placeholder={t('OptionalModel')}
              options={(modelsData?.paginatedData || []).map((item) => ({
                label: item.name || item.model,
                value: item._id,
              }))}
            />
          </Form.Item>
          <Typography.Text type="secondary">
            {t('VehicleModelHint')}
          </Typography.Text>
        </Form>
      </Modal>
    </div>
  );

  const columns = [
    {
      title: t('TransactionDate'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value) => (value ? unixToLocalDate(value) : 'N/A'),
    },
    {
      title: t('TransactionID'),
      dataIndex: 'ocppTransactionId',
      key: 'ocppTransactionId',
      render: (id, transcation) => (
        <Button type="link" onClick={() => setTransactionId(transcation._id)}>
          {id}
        </Button>
      ),
    },
    {
      title: t('ChargerName'),
      dataIndex: 'chargerData',
      key: 'chargerName',
      render: (chargerData) => (
        <span
          className="hover:text-primary cursor-pointer text-primary"
          onClick={() =>
            openChargerDrawer(chargerData?.[0]?._id, chargerData?.operatorId)
          }
        >
          {chargerData?.[0]?.name || 'N/A'}
        </span>
      ),
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag
          color={
            status === 'Completed'
              ? 'green'
              : status === 'InProgress'
                ? 'blue'
                : status === 'Interrupted'
                  ? 'cyan'
                  : 'red'
          }
          variant="outlined"
        >
          {status ? t(status) : 'N/A'}
        </Tag>
      ),
    },
  ];



  const handleViewRFID = (rfid) => {
    setRfidToView(rfid);
    setViewModalOpen(true);
  };

  const openChargerDrawer = (chargerId, operatorId) => {
    setChargerDetails({ id: chargerId, operatorId });
    setChargerDrawerOpen(true);
  };

  const closeChargerDrawer = () => {
    setChargerDrawerOpen(false);
    setChargerDetails(null);
  };


  const handleCloseTransactionModal = () => {
    setSelectedTransactionId(null);
    setTransactionModalOpen(false);
  };


  const renderTableContent = () => {
    if (isLoading || isFetching) {
      return (
        <div className="flex justify-center p-4">
          <Spin size="small" />
        </div>
      );
    }

    if (transactions.paginatedData.length > 0) {
      return (
        <Table
          columns={columns}
          size="middle"
          scroll={{ x: 'max-content', y: 40 * 8 }}
          dataSource={transactions.paginatedData}
          pagination={{
            current: page,
            size: 'default',
            total: transactions.totalCount,
            pageSize: 5,
            position: ["bottomCenter"],
            onChange: setPage,
            showSizeChanger: false,
          }}
          rowKey={(record) => record._id}
          className="mt-4"
        />
      );
    }

    return <Empty description={t('NoTransactions')} />;
  };

  return (
    <Drawer
      maskClosable={false}
      title={
        <div className="flex justify-between items-center">
          <Title level={4} className="mb-0!">
            {t('CustomerDetails')}
          </Title>
          <div className="flex gap-2">
            <Tooltip title={t('viewAnalysis')}>
              <Button
                type="text"
                icon={<BarChartOutlined />}
                onClick={() => {
                  navigate('/dashboard/reports', {
                    state: {
                      activeMainTab: 'otherReports',
                      activeOtherReportTab: 'userAnalysis',
                      selectedCustomer: customer._id,
                      selectedCustomerName: `${customer?.fname + " " +
                        customer?.lname
                        }`,
                      customerLabel: `+${customer.countryCode} ${customer.phoneNumber}`,
                    },
                  });
                  onClose();
                }}
              ></Button>
            </Tooltip>
            <Tooltip title={t('addRFIDCard')}>
              <Button
                type="text"
                disabled={isReadOnlyUser}
                onClick={() => setAddModalOpen(true)}
              >
                {t('addRFIDCard')}
              </Button>
            </Tooltip>
          </div>
        </div>
      }
      loading={isCustomerDetailsLoading}
      width={isMobile ? '100%' : 800}
      onClose={onClose}
      open={open}
    >
      <div className="flex justify-between mb-4">
        <Descriptions
          bordered
          column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }}
          size="small"
          className="flex-1"
          labelStyle={{ fontWeight: 600 }}
        >
          <Descriptions.Item label={t('Name')}>
            {customer?.fname && customer?.lname
              ? `${customer.fname.charAt(0).toUpperCase() + customer.fname.slice(1)} ${customer.lname.charAt(0).toUpperCase() +
              customer.lname.slice(1)
              }`
              : 'N/A'}
          </Descriptions.Item>
          {/* Email field removed */}
          <Descriptions.Item label={t('PhoneNumber')}>
            +{customer?.countryCode} {customer?.phoneNumber}
          </Descriptions.Item>
          <Descriptions.Item label={t('EmployeeId')}>
            {customer?.employeeId || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('FactoryNumber')}>
            {customer?.factoryNumber || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('StartCharge')}>
            {customer?.startChargeEnabled === true
              ? t('StartChargeEnabledLabel')
              : t('StartChargeDisabled')}
          </Descriptions.Item>
          <Descriptions.Item label={t('MonthlyReport')}>
            {customer?.monthlyReportEnabled === true
              ? t('MonthlyReportOn')
              : t('MonthlyReportOff')}
          </Descriptions.Item>
          <Descriptions.Item label={t('FreeTier')}>
            {customer?.freeTierEnabled ? t('FreeTierOn') : t('FreeTierOff')}
          </Descriptions.Item>
          {customer?.freeTierEnabled && (
            <Descriptions.Item label={t('FreeTierThreshold')}>
              {customer.freeTierUnit === 'CURRENCY'
                ? formatCurrency(Number(customer.freeTierMonthlyThreshold || 0))
                : `${Number(customer.freeTierMonthlyThreshold || 0)} kWh`}
            </Descriptions.Item>
          )}
          {customer?.freeTierEnabled &&
            customer.freeTierUnit !== 'CURRENCY' &&
            (customer?.freeTierUsedWh != null ||
              customer?.freeTierRemainingWh != null) && (
              <>
                <Descriptions.Item label={t('FreeTierUsed')}>
                  {customer?.freeTierUsedWh != null
                    ? `${(Number(customer.freeTierUsedWh) / 1000).toFixed(3)} kWh`
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('FreeTierRemaining')}>
                  {customer?.freeTierRemainingWh != null
                    ? `${(Number(customer.freeTierRemainingWh) / 1000).toFixed(3)} kWh`
                    : customer?.freeTierUsedWh != null
                      ? `${Math.max(
                          0,
                          Number(customer.freeTierMonthlyThreshold || 0) -
                            Number(customer.freeTierUsedWh) / 1000,
                        ).toFixed(3)} kWh`
                      : '-'}
                </Descriptions.Item>
              </>
            )}
          {customer?.freeTierEnabled &&
            customer.freeTierUnit === 'CURRENCY' &&
            (customer?.freeTierUsedAmount != null ||
              customer?.freeTierRemainingAmount != null) && (
              <>
                <Descriptions.Item label={t('FreeTierUsed')}>
                  {customer?.freeTierUsedAmount != null
                    ? formatCurrency(Number(customer.freeTierUsedAmount))
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('FreeTierRemaining')}>
                  {customer?.freeTierRemainingAmount != null
                    ? formatCurrency(Number(customer.freeTierRemainingAmount))
                    : customer?.freeTierUsedAmount != null
                      ? formatCurrency(
                          Math.max(
                            0,
                            Number(customer.freeTierMonthlyThreshold || 0) -
                              Number(customer.freeTierUsedAmount),
                          ),
                        )
                      : '-'}
                </Descriptions.Item>
              </>
            )}
          <Descriptions.Item label={t('IdleFee')}>
            {customer?.idleFeeEnabled === false
              ? t('StartChargeDisabled')
              : t('StartChargeEnabledLabel')}
          </Descriptions.Item>
          {customer?.idleFeeEnabled !== false && (
            <>
              <Descriptions.Item label={t('IdleFeeGraceLabel')}>
                {customer?.idleFeeGraceMinutes != null
                  ? t('IdleFeeOverrideValue', {
                      value: Number(customer.idleFeeGraceMinutes),
                    })
                  : t('IdleFeeCompanyDefault')}
              </Descriptions.Item>
              <Descriptions.Item label={t('IdleFeeRateLabel')}>
                {customer?.idleFeeRatePerMinute != null
                  ? t('IdleFeeOverrideValue', {
                      value: formatCurrency(
                        Number(customer.idleFeeRatePerMinute),
                      ),
                    })
                  : t('IdleFeeCompanyDefault')}
              </Descriptions.Item>
            </>
          )}
          {/* Wallet field removed */}
          <Descriptions.Item label={t('Status')}>
            {customer?.isBlocked ? t('Blocked') : t('Active')}
          </Descriptions.Item>
          <Descriptions.Item label={t('Verified')}>
            {customer?.isVerified ? t('Verified') : t('NotVerified')}
          </Descriptions.Item>
          {/* EmailVerified field removed */}
          <Descriptions.Item label={t('LoginBy')}>
            {customer?.loginBy ? t(customer.loginBy, { defaultValue: customer.loginBy }) : t('userDidntRegister')}
          </Descriptions.Item>
          <Descriptions.Item label={t('PreferredLanguage')}>
            {customer?.preferredLanguage || 'N/A'}
          </Descriptions.Item>

          <Descriptions.Item label={t('ClientPin')}>
            <div className="flex items-center gap-2">
              <span className="font-mono text-lg">
                {isPinVisible ? pinValue : '******'}
              </span>
              <Tooltip title={isPinVisible ? t('Hide') : t('Show')}>
                <Button
                  type="text"
                  icon={isPinVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  loading={isGettingPin}
                  disabled={!customer?._id}
                  onClick={() => {
                    if (isPinVisible) {
                      setIsPinVisible(false);
                      setPinValue(null);
                    } else {
                      if (customer?._id) {
                        getPin({ id: customer._id, operatorId });
                      }
                    }
                  }}
                />
              </Tooltip>
              <Tooltip title={t('Copy')}>
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  disabled={!isPinVisible || !pinValue}
                  onClick={() => {
                    navigator.clipboard.writeText(pinValue);
                    message.success(t('PinCopied'));
                  }}
                />
              </Tooltip>
              <Tooltip title={t('Reset')}>
                <Button
                  type="text"
                  icon={<SyncOutlined />}
                  disabled={!customer?._id}
                  onClick={() => {
                    if (customer?._id) {
                      setResetPinModalOpen(true);
                    }
                  }}
                />
              </Tooltip>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('AssignedRFID')}>
            <span className="inline-flex flex-wrap gap-2">
              {(customer?.rfids ?? customer?.rfidData)?.map((rfid) => (
                <Tooltip
                  title={rfid.isGlobal ? t('Global') : t('NotGlobal')}
                  key={rfid._id}
                >
                  <Tag
                    variant="outlined"
                    className={`cursor-pointer ${rfid.isGlobal ? 'text-gray-400' : 'text-blue-500 hover:underline'}`}
                    onClick={() => handleViewRFID(rfid)}
                  >
                    {rfid.RFID}
                  </Tag>
                </Tooltip>
              )) || 'N/A'}
            </span>
          </Descriptions.Item>
        </Descriptions>
      </div>

      <Tabs
        defaultActiveKey="1"
        items={[
          {
            key: '1',
            label: t('LastTransactions'),
            children: renderTableContent(),
          },
          {
            key: '2',
            label: t('Vehicles'),
            children: renderVehiclesContent(),
          },
        ]}
      />

      {/* View Transaction Modal */}
      {transactionId && (
        <TransactionDetailsDrawer
          id={transactionId}
          open={Boolean(transactionId)}
          onClose={() => setTransactionId(null)}
        />
      )}
      {addModalOpen && (
        <AddRFIDModalDirectlyUser
          open={addModalOpen}
          assignedTo={customer}
          setClose={() => setAddModalOpen(false)}
        />
      )}
      {viewModalOpen && rfidToView && (
        <ViewRFIDModal
          chargerData={rfidToView}
          open={viewModalOpen}
          setOpen={setViewModalOpen}
        />
      )}
      {chargerDrawerOpen && (
        <SingleChargerDrawer
          isOpen={chargerDrawerOpen}
          onClose={() => closeChargerDrawer()}
          selectedCharger={chargerDetails?.id}
          passoperatorId={chargerDetails?.operatorId}
        />
      )}
      <Modal
        title={<Title level={4} className="mb-0!">{t('ResetPinTitle')}</Title>}
        open={resetPinModalOpen}
        onCancel={() => {
          setResetPinModalOpen(false);
        }}
        onOk={() => {
          resetPin({ id: customer?._id });
        }}
        confirmLoading={isResettingPin}
      >
        <Typography.Text>{t('ResetPinConfirmation')}</Typography.Text>
      </Modal>
      {transactionModalOpen && (
        <TransactionDetailsDrawer
          open={transactionModalOpen}
          onClose={handleCloseTransactionModal}
          id={selectedTransactionId}
        />
      )}
    </Drawer>
  );
};

CustomerDrawerView.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  id: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      _id: PropTypes.string,
    }),
  ]).isRequired,
  /** When set (e.g. from a transaction row), used so details load if the global operator context is empty. */
  operatorId: PropTypes.string,
};

export default CustomerDrawerView;
